import Fuse from "fuse.js";
import {
  ComboboxItem,
  ComboboxParsedItem,
  ComboboxParsedItemGroup,
  OptionsFilter,
} from "@mantine/core";

const STOP_WORDS = new Set([
  "for",
  "the",
  "a",
  "an",
  "of",
  "with",
  "and",
  "or",
  "in",
  "to",
]);

function tokenize(search: string): string[] {
  return search
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0 && !STOP_WORDS.has(word.toLowerCase()));
}

function intersectAll(sets: Set<string>[]): Set<string> {
  if (sets.length === 0) return new Set();
  let result = sets[0];
  for (let i = 1; i < sets.length; i++) {
    result = new Set([...result].filter((v) => sets[i].has(v)));
  }
  return result;
}

interface FuzzySearchableItem {
  value: string;
  label: string;
  searchTerms: string[];
}

export function createFuzzyFilter(
  searchTermsMap: Record<string, string[]>,
): OptionsFilter {
  return ({ options, search }) => {
    if (!search.trim()) {
      return options;
    }

    const comboboxOptions = options as ComboboxItem[];

    const searchableItems: FuzzySearchableItem[] = comboboxOptions.map(
      (option) => ({
        value: option.value,
        label: option.label,
        searchTerms: searchTermsMap[option.value] ?? [],
      }),
    );

    const tokens = tokenize(search);
    if (tokens.length === 0) {
      return options;
    }

    const fuse = new Fuse(searchableItems, {
      keys: [
        { name: "label", weight: 2 },
        { name: "searchTerms", weight: 1 },
      ],
      threshold: 0.2,
      ignoreLocation: true,
      minMatchCharLength: 1,
    });

    const matchSets = tokens.map(
      (token) => new Set(fuse.search(token).map((result) => result.item.value)),
    );
    const matchedValues = intersectAll(matchSets);

    return comboboxOptions.filter((option) => matchedValues.has(option.value));
  };
}

export interface MedicationConditionPairItem {
  value: string;
  medicationName: string;
  conditionName: string;
  medicationAliases: string[];
  conditionAliases: string[];
}

function isGroup(item: ComboboxParsedItem): item is ComboboxParsedItemGroup {
  return "group" in item && "items" in item;
}

function filterGroupedOptions(
  options: ComboboxParsedItem[],
  matchedValues: Set<string>,
): ComboboxParsedItem[] {
  const result: ComboboxParsedItem[] = [];
  for (const option of options) {
    if (isGroup(option)) {
      const filtered = option.items.filter((item) =>
        matchedValues.has(item.value),
      );
      if (filtered.length > 0) {
        result.push({ group: option.group, items: filtered });
      }
    } else if (matchedValues.has(option.value)) {
      result.push(option);
    }
  }
  return result;
}

function buildPairFuse(pairs: MedicationConditionPairItem[]) {
  return new Fuse(pairs, {
    keys: [
      { name: "medicationName", weight: 3 },
      { name: "conditionName", weight: 2 },
      { name: "medicationAliases", weight: 2 },
      { name: "conditionAliases", weight: 1 },
    ],
    threshold: 0.3,
    ignoreLocation: true,
    minMatchCharLength: 1,
  });
}

export function createFuzzyPairFilter(
  pairs: MedicationConditionPairItem[],
): OptionsFilter {
  const fuse = buildPairFuse(pairs);

  return ({ options, search }) => {
    if (!search.trim()) {
      return options;
    }

    const tokens = tokenize(search);
    if (tokens.length === 0) {
      return options;
    }

    const matchSets = tokens.map(
      (token) => new Set(fuse.search(token).map((result) => result.item.value)),
    );
    const matchedValues = intersectAll(matchSets);

    return filterGroupedOptions(options, matchedValues);
  };
}

export function createPairMatcher(
  pairs: MedicationConditionPairItem[],
): (search: string) => MedicationConditionPairItem[] {
  const fuse = buildPairFuse(pairs);

  return (search: string) => {
    if (!search.trim()) {
      return pairs;
    }

    const tokens = tokenize(search);
    if (tokens.length === 0) {
      return pairs;
    }

    const matchSets = tokens.map(
      (token) => new Set(fuse.search(token).map((result) => result.item.value)),
    );
    const matchedValues = intersectAll(matchSets);

    return pairs.filter((pair) => matchedValues.has(pair.value));
  };
}
